import { ElementRef, Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class VideoStreamService {
    private stream!: MediaStream;
    private track!: MediaStreamTrack | null;

    constructor() {}

    // Start the webcam stream on basis of which cameraType i.e position is passed
    async startWebcam(
        cameraType: 'front' | 'back' = 'back'
    ): Promise<MediaStream> {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(
                (device) => device.kind === 'videoinput'
            );

            if (videoDevices.length === 0) {
                alert(
                    'No video devices found. Please refresh and allow camera access.'
                );
                throw new Error('No video devices available');
            }

            // back: { ideal: 'environment' },
            const cameraModes: Record<string, ConstrainDOMString> = {
                front: 'user',
                back: 'environment',
            };

            const constraints: MediaStreamConstraints = {
                video: {
                    facingMode: cameraModes[cameraType],
                },
                audio: false,
            };

            this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            this.track = this.stream.getVideoTracks()[0];

            this.resetZoom();

            return this.stream;
        } catch (err) {
            console.error('Error accessing webcam:', err);
            throw err;
        }
    }

    /**
    * Reset the camera zoom to its default state. This is necessary because for some cameras, after taking a picture, the zoom level may get altered.
    * We need to reset it to ensure the zoom returns to the default position.
    */
    private resetZoom() {
        const videoTrack = this.stream.getVideoTracks()[0];
        if (!videoTrack) {
            return;
        }

        videoTrack
            .applyConstraints({
                // @ts-ignore
                advanced: [{ zoom: 1 }],
            })
            .then(() => console.log('Zoom reset to 1x'))
            .catch((error) =>
                console.warn(
                    'Zoom reset failed (not supported on this device)',
                    error
                )
            );
    }

    // stop the webcam stream
    stopWebcam(): void {
        if (this.stream) {
            this.stream.getTracks().forEach((track) => track.stop());
        }
    }

    // Pass videoElement to start the current stream
    setVideoStream(videoElement: ElementRef) {
        if (videoElement?.nativeElement) {
            videoElement.nativeElement.srcObject = this.stream;
        }
    }

    // Pass videoElement to stop the current stream
    stopCurrentStream(videoElement) {
        videoElement.nativeElement.srcObject = null;
    }

    async toggleFlashlight(flashState): Promise<boolean> {
        if (!this.track) {
            console.warn('No active video track found.');
            return false;
        }

        const capabilities = this.track.getCapabilities();
        // @ts-ignore

        try {
            if (this.track && 'torch' in capabilities) {
                await this.track.applyConstraints({
                    // @ts-ignore
                    advanced: [{ torch: flashState }],
                });
                return flashState;
            } else {
                throw new DOMException(
                    'Torch is not supported on this device.'
                );
            }
        } catch (error) {
            console.error('Error toggling flashlight:', error);
        }

        return false;
    }
}
